// src/admin/hooks/useBorrower.ts
// Loads a borrower and everything the detail screen renders in one
// round-trip: credit_scores (current), loans (all), kyc_submissions
// (history). The admin_users referenced by borrower_notes are NOT
// joined here — that tab queries them separately because it paginates
// and filters independently of the rest of the detail view.
//
// All three embedded tables have FK → profiles(id), so the PostgREST
// embedded select works. Generated types have Relationships: [] (the
// types can't infer the shape) so we declare BorrowerDetail explicitly
// and cast via unknown — same pattern as useLoan.
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import type { Database } from '@/lib/database.types';

type Tables = Database['public']['Tables'];
type ProfileRow = Tables['profiles']['Row'];
type LoanRow = Tables['loans']['Row'];
type KycRow = Tables['kyc_submissions']['Row'];
type CreditScoreRow = Tables['credit_scores']['Row'];

export interface BorrowerDetail extends ProfileRow {
  credit_scores:
    | Pick<CreditScoreRow, 'current_score' | 'current_tier' | 'updated_at'>
    | Pick<CreditScoreRow, 'current_score' | 'current_tier' | 'updated_at'>[]
    | null;
  loans: Pick<
    LoanRow,
    'id' | 'loan_number' | 'status' | 'principal_ngwee' | 'total_repayable_ngwee' | 'applied_at' | 'due_date'
  >[];
  kyc_submissions: Pick<
    KycRow,
    'id' | 'status' | 'submitted_at' | 'reviewed_at' | 'ocr_result_jsonb'
  >[];
}

export function useBorrower(id: string | undefined) {
  return useQuery({
    queryKey: ['borrower', id],
    enabled: Boolean(id),
    queryFn: async (): Promise<BorrowerDetail> => {
      const { data, error } = await supabase
        .from('profiles')
        .select(
          `
          *,
          credit_scores(current_score, current_tier, updated_at),
          loans(id, loan_number, status, principal_ngwee, total_repayable_ngwee, applied_at, due_date),
          kyc_submissions(id, status, submitted_at, reviewed_at, ocr_result_jsonb)
        `,
        )
        .eq('id', id!)
        .single();
      if (error) throw error;
      return data as unknown as BorrowerDetail;
    },
  });
}

// Helper to normalize the PostgREST embedded one-to-one shape (which
// can arrive as `T`, `T[]`, or `null`) down to a single object or null.
export function pickCurrentScore(
  b: BorrowerDetail,
): Pick<CreditScoreRow, 'current_score' | 'current_tier' | 'updated_at'> | null {
  const cs = b.credit_scores;
  if (!cs) return null;
  if (Array.isArray(cs)) return cs[0] ?? null;
  return cs;
}
